import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Briefcase, TrendingUp, FileCheck, ChevronRight, DollarSign,
  Layers, Coins, ArrowUpRight, ArrowDownRight, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrencySpaced } from '@/lib/market';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, market) => formatCurrencySpaced(v, market);

function FMVCard({ label, value, sub, gain, icon: Icon, color }) {
  const isPos = gain >= 0;
  return (
    <Card className="bg-white border-[#e5e5e5] shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#4a4a4a] uppercase tracking-wide truncate mb-1">{label}</p>
            <p className="text-xl font-bold text-[#1a1a1a]">{value}</p>
            {sub && <p className="text-xs text-[#4a4a4a] mt-0.5">{sub}</p>}
            {gain != null && (
              <div className={`flex items-center gap-0.5 text-xs font-medium mt-1 ${isPos ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                {isPos ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(gain).toFixed(1)}% overall
              </div>
            )}
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { dealStageMap } = useAppData();
  const market = user?.market || 'IN';

  const [publicData,  setPublicData]  = useState(null);
  const [privateAUM,  setPrivateAUM]  = useState(0);
  const [othersAUM,   setOthersAUM]   = useState(0);
  const [recentDeals, setRecentDeals] = useState([]);
  const [closedDeals, setClosedDeals] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    const PRIVATE_CATS = ['vc', 'pe', 'search', 'long_only', 'credit', 'infra'];

    Promise.all([
      axios.get(`${API}/analytics/consolidation`).catch(() => ({ data: null })),
      ...PRIVATE_CATS.map(c => axios.get(`${API}/private-markets/${c}`).then(r => r.data || []).catch(() => [])),
      axios.get(`${API}/other-assets/gold`).catch(() => ({ data: [] })),
      axios.get(`${API}/other-assets/cash`).catch(() => ({ data: [] })),
      axios.get(`${API}/other-assets/alternatives`).catch(() => ({ data: [] })),
      axios.get(`${API}/deals/`).catch(() => ({ data: [] })),
    ]).then(([consolidationRes, ...rest]) => {
      const privateArrays = rest.slice(0, PRIVATE_CATS.length);
      const [goldRes, cashRes, altRes, dealsRes] = rest.slice(PRIVATE_CATS.length);

      setPublicData(consolidationRes.data);

      const privTotal = privateArrays.flat().reduce((s, d) => s + (d.current_value || d.nav || 0), 0);
      setPrivateAUM(privTotal);

      const gold = goldRes.data || [];
      const cash = cashRes.data || [];
      const alt  = altRes.data  || [];
      const goldVal = gold.reduce((s, g) => s + (g.units * g.current_price), 0);
      const cashVal = cash.reduce((s, c) => s + (c.balance || 0), 0);
      const altVal  = alt.reduce((s, a)  => s + (a.nav || 0), 0);
      setOthersAUM(goldVal + cashVal + altVal);

      const deals = dealsRes.data || [];
      setRecentDeals(deals.slice(0, 5));
      setClosedDeals(deals.filter(d => d.stage === 'closed'));
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const publicAUM    = publicData?.total_value    || 0;
  const publicGain   = publicData?.gain_percent   || null;
  const totalFMV     = publicAUM + privateAUM + othersAUM;
  const getStageColor = (stage) => dealStageMap[stage]?.data?.color || 'bg-[#a8a8a8]';

  return (
    <div className="p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
          <Briefcase className="w-5 h-5 text-[#1a1a1a]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Dashboard</h1>
          <p className="text-[#4a4a4a] mt-1">Welcome back, {user?.name}</p>
        </div>
      </div>

      {/* FMV Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <FMVCard
          label="Total FMV"
          value={loading ? '—' : fmt(totalFMV, market)}
          sub="across all asset classes"
          gain={null}
          icon={DollarSign}
          color="#1a1a1a"
        />
        <FMVCard
          label="Public Markets"
          value={loading ? '—' : fmt(publicAUM, market)}
          sub={publicData ? `${publicData.holdings_count || 0} holdings` : 'listed securities'}
          gain={publicGain}
          icon={TrendingUp}
          color="#4a9b6f"
        />
        <FMVCard
          label="Private Markets"
          value={loading ? '—' : fmt(privateAUM, market)}
          sub={`${closedDeals.length} closed investment${closedDeals.length !== 1 ? 's' : ''}`}
          gain={null}
          icon={Layers}
          color="#9b8dc4"
        />
        <FMVCard
          label="Others"
          value={loading ? '—' : fmt(othersAUM, market)}
          sub="gold · cash · alternatives"
          gain={null}
          icon={Coins}
          color="#c9a86b"
        />
      </div>

      {/* Recent Deals + Quick Actions */}
      <div className="grid grid-cols-2 gap-6">
        <Card className="bg-white border-[#e5e5e5] shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#1a1a1a] text-base">Recent Deals</CardTitle>
              <Link to="/deals" className="text-xs text-[#4a4a4a] hover:text-[#1a1a1a] flex items-center gap-1">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-14 bg-[#f5f5f5] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : recentDeals.length === 0 ? (
              <p className="text-[#4a4a4a] text-sm text-center py-8">No deals yet</p>
            ) : (
              <div className="space-y-2">
                {recentDeals.map((deal) => (
                  <Link
                    key={deal.id}
                    to={`/deals/${deal.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#f9f9f9] border border-transparent hover:border-[#e5e5e5] transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStageColor(deal.stage)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1a1a1a] truncate">{deal.deal_name}</p>
                      <p className="text-xs text-[#4a4a4a]">{deal.company_name}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm text-[#1a1a1a]">{fmt(deal.target_investment_amount || 0, market)}</p>
                      <Badge className={`text-xs border-0 mt-0.5 ${
                        deal.stage === 'closed' ? 'bg-emerald-100 text-emerald-700' :
                        deal.stage === 'passed' ? 'bg-[#c97a3a]/10 text-[#c97a3a]' :
                        'bg-[#ffd879]/20 text-[#1a1a1a]'
                      }`}>{deal.stage?.replace(/_/g, ' ')}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#e5e5e5] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#1a1a1a] text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link
              to="/private-markets"
              className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#dae4c4] hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#dae4c4] flex items-center justify-center">
                <Layers className="w-5 h-5 text-[#1a1a1a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a1a1a]">Private Markets</p>
                <p className="text-xs text-[#4a4a4a]">PE · VC · Search Funds · Long-Only</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#4a4a4a] ml-auto" />
            </Link>

            <Link
              to="/deals"
              className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#dae4c4] hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#dae4c4] flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#1a1a1a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a1a1a]">Deal Pipeline</p>
                <p className="text-xs text-[#4a4a4a]">Track deals from sourcing to close</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#4a4a4a] ml-auto" />
            </Link>

            <Link
              to="/approvals"
              className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#dae4c4] hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#dae4c4] flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#1a1a1a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a1a1a]">Pending Approvals</p>
                <p className="text-xs text-[#4a4a4a]">Review and approve requests</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#4a4a4a] ml-auto" />
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
